import { Checkbox } from "@/components/ui/checkbox"

import type { FacetControlsProps, FacetDescriptor } from "./types"

/**
 * Generic presentation over facet descriptors: one labelled group per facet
 * with a checkbox per option and its count. Toggling reports the facet's
 * identifier and the option's value, for the consumer to hand to the
 * module's `toggleFacet` command. A consumer may render this or lay the
 * descriptors into its own controls.
 */
export function FacetControls({ facets, onToggle }: FacetControlsProps) {
  return (
    <div className="flex flex-col gap-4" data-testid="facet-controls">
      {facets.map((facet) => (
        <FacetControl key={facet.id} facet={facet} onToggle={onToggle} />
      ))}
    </div>
  )
}

function FacetControl({
  facet,
  onToggle,
}: {
  facet: FacetDescriptor
  onToggle: FacetControlsProps["onToggle"]
}) {
  if (facet.options.length === 0) return null

  return (
    <fieldset
      className="flex flex-col gap-1.5"
      data-testid="facet-control"
      data-facet-id={facet.id}
    >
      <legend className="pb-1 text-sm font-medium">{facet.label}</legend>
      {facet.options.map((option) => (
        <label
          key={option.value}
          className="flex min-w-0 cursor-pointer items-center gap-2 text-sm"
        >
          <Checkbox
            aria-label={option.label}
            checked={option.selected}
            onCheckedChange={() => onToggle(facet.id, option.value)}
          />
          <span className="min-w-0 flex-1 break-words">{option.label}</span>
          <span className="text-muted-foreground shrink-0 tabular-nums">
            {option.count}
          </span>
        </label>
      ))}
    </fieldset>
  )
}
